<template>
  <div class="transaction-details-wrapper">
    <v-card class="detail-card mb-4" elevation="1">
      <v-card-title class="section-title">
        <i class="fas fa-info-circle mr-2"></i>
        Informações da Transação
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Operação</span>
              <span class="detail-value">{{ operationType }}</span>
              <v-chip v-if="isChargeback" color="error" small class="ml-2">
                Estornada
              </v-chip>
            </div>
          </v-col>

          <v-col v-if="transaction.origin" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Responsável</span>
              <span class="detail-value">{{ responsibleName }}</span>
            </div>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Data/Hora</span>
              <span class="detail-value">{{ formattedDate }}</span>
            </div>
          </v-col>

          <v-col v-if="showTarget" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Destino</span>
              <span class="detail-value">{{ targetName }}</span>
            </div>
          </v-col>

          <v-col v-if="showIdentification" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">CPF/CNPJ</span>
              <span class="detail-value">{{ targetIdentification }}</span>
            </div>
          </v-col>

          <v-col v-if="transaction.reason" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Justificativa</span>
              <span class="detail-value">{{ transaction.reason }}</span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="hasSpecificDetails" class="detail-card mb-4" elevation="1">
      <v-card-title class="section-title">
        <i class="fas fa-list mr-2"></i>
        Detalhes Específicos
      </v-card-title>
      <v-card-text>
        <v-row v-if="isRegularizationType">
          <v-col v-if="hasRegularizedNotifications" cols="12">
            <div class="detail-field">
              <span class="detail-label">Notificações Regularizadas</span>
              <div
                  v-for="notification in regularizedNotifications"
                  :key="notification.id"
              >
                <span class="detail-value">
                  ID: {{ notification.id }} - Placa: {{ notification.plate }} -
                  {{ formatDateTime(notification.date_time) }}
                </span>
              </div>
            </div>
          </v-col>
          <v-col v-if="bonusBeneficiary" cols="12" sm="6">
            <div class="detail-field">
              <span class="detail-label">Beneficiário do Bônus</span>
              <span class="detail-value">{{ bonusBeneficiary }}</span>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="isActivationType">
          <v-col v-if="activationDetails.plate" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Placa</span>
              <span class="detail-value">{{ activationDetails.plate }}</span>
            </div>
          </v-col>
          <v-col
              v-if="activationDetails.dateTimeBegin"
              cols="12"
              sm="6"
              md="4"
          >
            <div class="detail-field">
              <span class="detail-label">Início</span>
              <span class="detail-value">{{
                  formatDateTime(activationDetails.dateTimeBegin)
                }}</span>
            </div>
          </v-col>
          <v-col v-if="activationDetails.dateTimeEnd" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Fim</span>
              <span class="detail-value">{{
                  formatDateTime(activationDetails.dateTimeEnd)
                }}</span>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="isSpecialTransactionType">
          <v-col
              v-if="specialTransactionDetails.creatorName"
              cols="12"
              sm="6"
              md="4"
          >
            <div class="detail-field">
              <span class="detail-label">Criador</span>
              <span class="detail-value">{{
                  specialTransactionDetails.creatorName
                }}</span>
            </div>
          </v-col>
          <v-col
              v-if="specialTransactionDetails.agentName"
              cols="12"
              sm="6"
              md="4"
          >
            <div class="detail-field">
              <span class="detail-label">Agente</span>
              <span class="detail-value">{{
                  specialTransactionDetails.agentName
                }}</span>
            </div>
          </v-col>
          <v-col v-if="hasAssociatedPayments" cols="12">
            <div class="detail-field">
              <span class="detail-label">Pagamentos Associados</span>
              <div
                  v-for="payment in associatedPayments"
                  :key="payment.id"
              >
                <span class="detail-value">
                  {{ payment.payment_form.name }}:
                  {{ formatMoney(payment.total_amount) }}
                </span>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="hasItems" class="detail-card mb-4" elevation="1">
      <v-card-title class="section-title">
        <i class="fas fa-box-open mr-2"></i>
        Itens
      </v-card-title>
      <v-card-text>
        <v-row v-for="(item, index) in transaction.items" :key="index">
          <v-col cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Produto</span>
              <span class="detail-value">{{ item.product.name }}</span>
            </div>
          </v-col>
          <v-col v-if="shouldShowQuantity(item)" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Quantidade</span>
              <span class="detail-value">{{ item.quantity }}</span>
            </div>
          </v-col>
          <v-col v-if="shouldShowPrice(item)" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Valor</span>
              <span class="detail-value">{{
                  formatMoney(item.quantity * item.price)
                }}</span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="hasPayments" class="detail-card mb-4" elevation="1">
      <v-card-title class="section-title">
        <i class="fas fa-dollar-sign mr-2"></i>
        Pagamento
      </v-card-title>
      <v-card-text>
        <v-row v-for="payment in transaction.payments" :key="payment.id">
          <v-col cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">Forma de Pagamento</span>
              <span class="detail-value">{{ payment.payment_form.name }}</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="detail-field">
              <span class="detail-label">Valor</span>
              <span class="detail-value">{{ formatMoney(payment.amount) }}</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <div class="detail-field">
              <span class="detail-label">Taxa</span>
              <span class="detail-value">{{ formatMoney(payment.tax) }}</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="detail-field">
              <span class="detail-label">Valor Total</span>
              <span class="detail-value price-value">{{
                  formatMoney(payment.total_amount)
                }}</span>
            </div>
          </v-col>
          <v-col v-if="payment.nsu" cols="12" sm="6" md="4">
            <div class="detail-field">
              <span class="detail-label">NSU</span>
              <span class="detail-value">{{ payment.nsu }}</span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "TransactionDetails",
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    specificDetails: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    operationType() {
      const transactionType = this.transaction.type;
      const plate = this.transaction.plate || "";

      if (transactionType === 17) {
        return `Regularização do veículo ${plate}`;
      }

      if (transactionType === 6) {
        return `Estacionamento ${plate}`;
      }

      if (transactionType === 8) {
        return `Transferência de créditos`;
      }

      return this.transaction.type_name || "Transação";
    },
    isChargeback() {
      const hasChargeback =
          this.transaction.chargeback &&
          this.transaction.chargeback.id !== null &&
          this.transaction.type !== 10;

      const hasDevolution =
          this.transaction.devolution &&
          this.transaction.devolution.id !== null &&
          this.transaction.type !== 10;

      return hasChargeback || hasDevolution;
    },
    responsibleName() {
      const hasOrigin = this.transaction.origin;

      if (!hasOrigin) {
        return "";
      }

      const userType = this.getUserTypeName(this.transaction.origin.user_type);
      const userName = this.transaction.origin.name;

      return `${userType} ${userName}`;
    },
    formattedDate() {
      const date = new Date(this.transaction.created_at);
      return date.toLocaleString("pt-BR");
    },
    showTarget() {
      const hasTarget = this.transaction.target;
      const isExcludedType = [2, 13].includes(this.transaction.type);

      return hasTarget && !isExcludedType;
    },
    targetName() {
      const hasTarget = this.transaction.target;

      if (hasTarget) {
        return this.transaction.target.name;
      }

      return "";
    },
    showIdentification() {
      const hasTarget = this.transaction.target;
      const isCreditSale = this.transaction.type === 1;

      return hasTarget && isCreditSale;
    },
    targetIdentification() {
      const hasTarget = this.transaction.target;

      if (hasTarget) {
        return this.transaction.target.identification;
      }

      return "";
    },
    hasSpecificDetails() {
      const hasDetails = this.specificDetails;
      const hasKeys = hasDetails && Object.keys(this.specificDetails).length > 0;

      return hasKeys;
    },
    isRegularizationType() {
      return this.transaction.type === 17;
    },
    isActivationType() {
      return this.transaction.type === 6;
    },
    isSpecialTransactionType() {
      const specialTypes = [30, 31];
      return specialTypes.includes(this.transaction.type);
    },
    hasRegularizedNotifications() {
      const notifications = this.regularizedNotifications;
      return notifications && notifications.length > 0;
    },
    regularizedNotifications() {
      return this.specificDetails.transactions || [];
    },
    bonusBeneficiary() {
      return this.specificDetails.wallet_transfer || null;
    },
    activationDetails() {
      return {
        plate: this.specificDetails.plate || null,
        time: this.specificDetails.time || null,
        dateTimeBegin: this.specificDetails.date_time_begin || null,
        dateTimeEnd: this.specificDetails.date_time_end || null,
      };
    },
    specialTransactionDetails() {
      return {
        creatorName: this.specificDetails.creatorName || null,
        agentName: this.specificDetails.agentName || null,
      };
    },
    hasAssociatedPayments() {
      const payments = this.associatedPayments;
      return payments && payments.length > 0;
    },
    associatedPayments() {
      return this.specificDetails.payments || [];
    },
    hasItems() {
      const items = this.transaction.items;
      return items && items.length > 0;
    },
    hasPayments() {
      const payments = this.transaction.payments;
      return payments && payments.length > 0;
    },
  },
  methods: {
    formatDateTime(dateTime) {
      const hasValidDateTime = dateTime;

      if (!hasValidDateTime) {
        return "";
      }

      const date = new Date(dateTime);
      return date.toLocaleString("pt-BR");
    },
    formatMoney(amount) {
      const hasValidAmount = amount;

      if (!hasValidAmount) {
        return "R$ 0,00";
      }

      const value = amount / 100;
      const formattedValue = value.toFixed(2).replace(".", ",");

      return `R$ ${formattedValue}`;
    },
    getUserTypeName(userType) {
      const types = {
        1: "Agente",
        2: "Motorista",
        3: "Sistema",
      };

      return types[userType] || "";
    },
    shouldShowQuantity(item) {
      const showQuantityTypes = [2, 3, 5, 13];
      return showQuantityTypes.includes(this.transaction.type);
    },
    shouldShowPrice(item) {
      const hidepriceTypes = [3, 13, 15, 16];
      const shouldHide = hidepriceTypes.includes(this.transaction.type);

      return !shouldHide;
    },
  },
};
</script>

<style scoped>
.transaction-details-wrapper {
  width: 100%;
}

.detail-card {
  background-color: #fff;
  border-radius: 8px;
  width: 100%;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  padding: 12px 16px;
  background-color: #f5f5f5;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
}

.detail-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  word-break: break-word;
}

.price-value {
  font-size: 16px;
  color: #1aa23f;
  font-weight: bold;
}
</style>
